import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { setGlobalErrorHandler } from '../services/apiClient';
import { ServiceError } from '../services/utils/apiErrorUtils';
import { 
  ErrorNotificationManager, 
  useErrorNotifications,
  ErrorNotification,
  NotificationType 
} from '../components/common/ErrorNotification';

interface ErrorNotificationContextType {
  notifications: ErrorNotification[];
  addNotification: (notification: Omit<ErrorNotification, 'id' | 'timestamp'>) => string;
  addError: (error: ServiceError, title?: string) => string;
  addSuccess: (message: string, title?: string) => string;
  addWarning: (message: string, title?: string) => string;
  addInfo: (message: string, title?: string) => string;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const ErrorNotificationContext = createContext<ErrorNotificationContextType | undefined>(undefined);

interface ErrorNotificationProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxNotifications?: number;
}

export const ErrorNotificationProvider: React.FC<ErrorNotificationProviderProps> = ({
  children,
  position = 'top-right',
  maxNotifications = 5,
}) => {
  const {
    notifications,
    addNotification,
    addError,
    removeNotification,
    clearAll,
  } = useErrorNotifications();

  // Set up global error handler for API client
  useEffect(() => {
    const errorHandler = (error: ServiceError) => {
      addError(error);
    };
    
    setGlobalErrorHandler(errorHandler);

    // Clean up on unmount
    return () => {
      setGlobalErrorHandler(() => {});
    };
  }, [addError]);

  // Helper methods for different notification types
  const addSuccess = (message: string, title: string = 'Success') => {
    return addNotification({
      type: 'success',
      title,
      message,
      autoClose: true,
      duration: 4000,
    });
  };

  const addWarning = (message: string, title: string = 'Warning') => {
    return addNotification({
      type: 'warning',
      title,
      message,
      autoClose: true,
      duration: 6000,
    });
  };

  const addInfo = (message: string, title: string = 'Information') => {
    return addNotification({
      type: 'info',
      title,
      message,
      autoClose: true,
      duration: 5000,
    });
  };

  const contextValue: ErrorNotificationContextType = {
    notifications,
    addNotification,
    addError,
    addSuccess,
    addWarning,
    addInfo,
    removeNotification,
    clearAll,
  };

  return (
    <ErrorNotificationContext.Provider value={contextValue}>
      {children}
      <ErrorNotificationManager
        notifications={notifications}
        onClose={removeNotification}
        position={position}
        maxNotifications={maxNotifications}
      />
    </ErrorNotificationContext.Provider>
  );
};

// Hook to use error notifications
export const useErrorNotificationContext = (): ErrorNotificationContextType => {
  const context = useContext(ErrorNotificationContext);
  if (context === undefined) {
    throw new Error('useErrorNotificationContext must be used within an ErrorNotificationProvider');
  }
  return context;
};

// Global notification functions that can be used outside of React components
let globalNotificationContext: ErrorNotificationContextType | null = null;

export const setGlobalNotificationContext = (context: ErrorNotificationContextType | null) => {
  globalNotificationContext = context;
};

// Global helper functions
export const showGlobalError = (error: ServiceError | string, title?: string) => {
  if (!globalNotificationContext) {
    console.warn('Global notification context not available');
    return;
  }
  
  if (typeof error === 'string') {
    globalNotificationContext.addNotification({
      type: 'error',
      title: title || 'Error',
      message: error,
    });
  } else {
    globalNotificationContext.addError(error, title);
  }
};

export const showGlobalSuccess = (message: string, title?: string) => {
  if (!globalNotificationContext) {
    console.warn('Global notification context not available');
    return;
  }
  globalNotificationContext.addSuccess(message, title);
};

export const showGlobalWarning = (message: string, title?: string) => {
  if (!globalNotificationContext) {
    console.warn('Global notification context not available');
    return;
  }
  globalNotificationContext.addWarning(message, title);
};

export const showGlobalInfo = (message: string, title?: string) => {
  if (!globalNotificationContext) {
    console.warn('Global notification context not available');
    return;
  }
  globalNotificationContext.addInfo(message, title);
};

// Hook to set up global context (for use in top-level components)
export const useSetupGlobalNotifications = () => {
  const context = useErrorNotificationContext();
  
  useEffect(() => {
    setGlobalNotificationContext(context);
    return () => {
      setGlobalNotificationContext(null);
    };
  }, [context]);
};

export default ErrorNotificationProvider;